// AiCandidateActions — per-row Approve / Reject controls for a candidate.
//
// Per WP-9 design rule (masterplan §7.9 and §8.9): Approve and Reject are
// TONAL buttons (variant="secondary"), NOT destructive. Publish — which is
// a card-level CTA, not a per-row action — uses `variant="primary"` and is
// guarded by a `<Dialog>` confirmation in `AIReviewToolCard.tsx`.

import { Button, Icon, Stack, tokens } from "@/design";

export interface AiCandidateActionsProps {
  readonly disabled?: boolean;
  readonly approved?: boolean;
  readonly rejected?: boolean;
  readonly busy?: boolean;
  readonly onApprove: () => void;
  readonly onReject: () => void;
  readonly approveAriaLabel?: string;
  readonly rejectAriaLabel?: string;
}

export function AiCandidateActions({
  disabled,
  approved,
  rejected,
  busy,
  onApprove,
  onReject,
  approveAriaLabel,
  rejectAriaLabel,
}: AiCandidateActionsProps) {
  const isApproveDisabled = Boolean(disabled || approved || busy);
  const isRejectDisabled = Boolean(disabled || rejected || busy);
  return (
    <Stack
      direction="row"
      gap={1}
      style={{ marginTop: tokens.space[2], flexWrap: "wrap" }}
      data-testid="ai-candidate-actions"
    >
      <Button
        variant="secondary"
        disabled={isApproveDisabled}
        onClick={onApprove}
        aria-label={approveAriaLabel}
        data-testid="ai-candidate-approve"
        style={{
          minHeight: tokens.density.touchMin,
          display: "inline-flex",
          alignItems: "center",
          gap: tokens.space[1],
        }}
      >
        <Icon name="check_circle" size={20} decorative />
        <span>Approve</span>
      </Button>
      <Button
        variant="secondary"
        disabled={isRejectDisabled}
        onClick={onReject}
        aria-label={rejectAriaLabel}
        data-testid="ai-candidate-reject"
        style={{
          minHeight: tokens.density.touchMin,
          display: "inline-flex",
          alignItems: "center",
          gap: tokens.space[1],
        }}
      >
        <Icon name="cancel" size={20} decorative />
        <span>Reject</span>
      </Button>
    </Stack>
  );
}
